import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, Button, Table, Space, message, notification, Spin, Modal, Alert, Tag, Typography, Descriptions } from "antd";
import { UploadOutlined, DeleteOutlined, AimOutlined, FieldTimeOutlined, ArrowRightOutlined, DownloadOutlined } from "@ant-design/icons";
import { useAuth } from "../../../hooks/useAuth";
import PermissionGuard from "../../../components/auth/PermissionGuard";
import AircraftTag from "../../../components/AircraftTag";
import LegCostFilter from "./components/LegCostFilter";
import LegCostModal from "./components/LegCostModal";
import LegCostDrillDownDrawer from "./components/LegCostDrillDownDrawer";
import BatchDeleteModal from "./components/BatchDeleteModal";
import FrontendImportModal from "../../../components/import/FrontendImportModal";

import { settlementService } from "../../../api/enhancedApi";
import { exportLegCostListToExcel } from "../../../utils/excelUtils";
import { generateExportFilename } from "../../../utils/exportUtils";
import dayjs from "dayjs";
import styles from "./LegCostManagementPage.module.css";

// 千分位 + 两位小数,无符号(给金额单元格用)
const formatMoney = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "0.00";
    return n.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

// 前端列 key(小写) → 后端 cost_type 标准值(大写)
// 任何新增 / 改动必须同步 backend/services/settlement/cost_type_alias.py + COST_TYPE_OPTIONS
const COL_KEY_TO_COST_TYPE = {
    bh: "BH",
    fh: "FH",
    fuel: "FUEL",
    navigation: "NAVIGATION",
    ground_handling: "GROUND_HANDLING",
    landing_parking: "LANDING_PARKING",
    catering: "CATERING",
    transportation: "TRANSPORTATION",
    traffic_rights_permit: "TRAFFIC_RIGHTS_PERMIT",
    positioning: "POSITIONING",
    uld: "ULD",
    sita: "SITA",
    deicing: "DEICING",
    accommodation: "ACCOMMODATION",
    laundry: "LAUNDRY",
    disinfection: "DISINFECTION",
    flight_service_charge: "FLIGHT_SERVICE_CHARGE",
    others_fee: "OTHERS_FEE",
    trucking_fee: "TRUCKING_FEE",
    cargo_handling: "CARGO_HANDLING",
    traffic_rights: "TRAFFIC_RIGHTS",
    acmi: "ACMI",
    commission: "COMMISSION",
};

const LegCostManagementPage = () => {
    // 获取权限相关功能
    const { hasPermission, loading: authLoading, user } = useAuth();

    // 🚀 使用useMemo缓存默认日期范围，避免重复创建对象
    const defaultDateConfig = useMemo(() => {
        const today = dayjs();
        const oneMonthAgo = today.subtract(1, 'month');
        return {
            start_date: oneMonthAgo.format('YYYY-MM-DD'),
            end_date: today.format('YYYY-MM-DD'),
            defaultDateRange: [oneMonthAgo, today] // 用于RangePicker的默认值
        };
    }, []); // 空依赖数组，只在组件挂载时计算一次

    // 状态定义 - 包含默认日期范围
    const [filterParams, setFilterParams] = useState(() => ({
        start_date: defaultDateConfig.start_date,
        end_date: defaultDateConfig.end_date
    }));
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
    });
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedLeg, setSelectedLeg] = useState(null);
    const [prefilledCostType, setPrefilledCostType] = useState(undefined);
    const [importModalVisible, setImportModalVisible] = useState(false);

    // S4 下钻抽屉(T21-T26b)
    const [drillDownState, setDrillDownState] = useState({
        visible: false,
        flightOperationId: null,
        costType: null,
        batchId: null,
        // 存整条航段记录:抽屉里点"在此录入费用"时,把航段(航班号/航段/日期)一起带进录入弹窗
        record: null,
    });

    // spec S2 (2026-05-27) — BH/FH 数据来源详情 Modal
    // BH/FH 不再走"账单明细抽屉"(数据通路不同),改为弹窗显示来源(SFD / MVT / NONE) + 数值
    // 数据从主表 record 直接读,不需要额外 API 调用
    const [bhFhDetailModal, setBhFhDetailModal] = useState({
        visible: false,
        record: null,
        costType: null,
    });

    // 2026-05-13 决策 14:从数据异常工作台「定位」按钮跳过来时的锚点信息,
    // 用于在表格上方展示一条提示 banner(已定位到航段 X,请重点查看 Y 列)+「显示所有」清除入口
    const [locatedAnchor, setLocatedAnchor] = useState(null);

    // 批量选择相关状态
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [batchDeleteModalVisible, setBatchDeleteModalVisible] = useState(false);

    // 导出状态
    const [exporting, setExporting] = useState(false);

    // S4 路由参数:支持从 S3 异常工作台 / 批次详情页直接跳过来定位航段 + 费用类型 + 批次
    const location = useLocation();
    const navigate = useNavigate();

    // 批量删除后,若影响到已完成批次,弹"前往刷新"通知(与 LegCostModal 录入体验一致)
    const [notifyApi, contextHolder] = notification.useNotification();

    // 使用真实API获取数据
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({
        items: [],
        pagination: {
            current: 1,
            pageSize: 20,
            total: 0
        }
    });

    // 获取航段费用列表
    const loadLegCosts = useCallback(async (filters = {}) => {
        try {
            setLoading(true);
            
            const params = {
                page: filters.page || 1,
                page_size: filters.pageSize || 20,
                ...filters
            };
            
            console.log("正在获取航段费用数据，参数:", params);
            
            const response = await settlementService.legCost.getLegCostList(params);
            
            // 修复：axios响应数据在response.data中
            const responseData = response.data;
            
            if (responseData && Array.isArray(responseData.items)) {
                setData({
                    items: responseData.items,
                    pagination: responseData.pagination || {
                        current: 1,
                        pageSize: 20,
                        total: responseData.items.length
                    },
                    summary: responseData.summary || null,
                });
                
                if (responseData.items.length > 0) {
                    console.log("航段费用数据加载成功:", responseData);
                } else {
                    console.log("航段费用查询成功，暂无数据");
                    // 数据为空时不显示任何提示
                }
            } else {
                console.warn("API返回数据格式异常:", responseData);
                message.warning("数据格式异常");
            }
        } catch (error) {
            console.error("获取航段费用数据失败:", error);
            message.error("获取数据失败: " + (error.message || "未知错误"));
        } finally {
            setLoading(false);
        }
    }, []);

    // 🚨 修复：统一的数据加载逻辑，避免重复调用
    React.useEffect(() => {
        const params = {
            page: pagination.current,
            pageSize: pagination.pageSize,
            ...filterParams
        };
        
        console.log("useEffect触发数据加载，参数:", params);
        loadLegCosts(params);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterParams, pagination.current, pagination.pageSize, loadLegCosts]);

    // S4 — 监听 URL query,从 S3 异常工作台 / 批次详情跳过来时定位到目标航段
    // 支持参数:
    //   ?flight_operation_id=123&cost_type=FUEL                → 列表只过滤这一行 + banner 提示
    //   ?flight_operation_id=123&cost_type=FUEL&batch_id=456   → 同上(batch_id 仅用于审计)
    //   from=anomaly / from=batch_detail / from=profit_report  → 仅用于审计日志(B12)
    //
    // 2026-05-13 决策 14:
    //   - 原本支持 ?action=add(直接弹补录窗口),已废弃
    //   - 原本默认行为是"自动打开下钻抽屉",体验过重(用户只想先看一眼)
    //   - 新行为:setFilterParams 加 flight_operation_id,列表只显示目标航段 +
    //     页面顶部显示蓝色 banner 提示业务"已定位到航段 X,请重点查看 Y 列"
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const flightOpId = params.get("flight_operation_id");
        const costTypeParam = params.get("cost_type");
        const batchIdParam = params.get("batch_id");
        if (!flightOpId || !costTypeParam) return;
        const fid = Number(flightOpId);
        const ctUpper = String(costTypeParam).toUpperCase();
        if (Number.isNaN(fid)) return;

        setFilterParams((prev) => ({
            ...prev,
            flight_operation_id: fid,
            start_date: undefined,
            end_date: undefined,
        }));
        setPagination((prev) => ({ ...prev, current: 1 }));
        setLocatedAnchor({
            flightOperationId: fid,
            costType: ctUpper,
            batchId: batchIdParam ? Number(batchIdParam) : undefined,
        });

        // 清理 URL query,避免 refresh 后又自动应用过滤
        const cleaned = new URLSearchParams(location.search);
        ["flight_operation_id", "cost_type", "batch_id", "from", "action"].forEach((k) =>
            cleaned.delete(k)
        );
        navigate(
            { pathname: location.pathname, search: cleaned.toString() ? `?${cleaned}` : "" },
            { replace: true }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // 「显示所有」— 清掉定位过滤,恢复默认列表(包括默认日期范围)
    const handleClearLocation = useCallback(() => {
        setFilterParams((prev) => {
            const next = { ...prev };
            delete next.flight_operation_id;
            // 定位时清掉了日期范围(防止历史告警被日期筛掉),恢复时回到默认 30 天
            next.start_date = defaultDateConfig.start_date;
            next.end_date = defaultDateConfig.end_date;
            return next;
        });
        setPagination((prev) => ({ ...prev, current: 1 }));
        setLocatedAnchor(null);
    }, [defaultDateConfig]);

    // 渲染费用单元格(保留为下钻入口待用,目前主要走 renderCostAmount,见下方表格列定义)
    // eslint-disable-next-line no-unused-vars
    const renderCostCell = (value) => {
        const amount = parseFloat(value) || 0;
        return (
            <span
                style={{
                    color: amount === 0 ? "#ff4d4f" : "#1890ff",
                    fontWeight: amount === 0 ? "normal" : "500",
                }}
            >
                ${amount.toFixed(2)}
            </span>
        );
    };

    // 费用类型列定义 — 对账需要看见 ACMI / 佣金 / 货物操作，不再从主表隐去
    const COST_TYPES = useMemo(
        () => [
            { key: "bh", title: "BH", isHours: true },
            { key: "fh", title: "FH", isHours: true },
            { key: "fuel", title: "Fuel" },
            { key: "navigation", title: "Navigation" },
            { key: "ground_handling", title: "Ground Handling" },
            { key: "landing_parking", title: "Landing/Parking" },
            { key: "catering", title: "Catering" },
            { key: "transportation", title: "Transportation" },
            { key: "traffic_rights_permit", title: "Traffic Rights Permit" },
            { key: "traffic_rights", title: "Traffic Rights" },
            { key: "positioning", title: "Positioning" },
            { key: "uld", title: "ULD" },
            { key: "sita", title: "SITA" },
            { key: "deicing", title: "Deicing" },
            { key: "accommodation", title: "Accommodation" },
            { key: "laundry", title: "Laundry" },
            { key: "disinfection", title: "Disinfection" },
            { key: "flight_service_charge", title: "Flight Service Charge" },
            { key: "trucking_fee", title: "Trucking Fee" },
            { key: "cargo_handling", title: "Cargo Handling" },
            { key: "acmi", title: "ACMI" },
            { key: "commission", title: "Commission" },
            { key: "others_fee", title: "Others" },
        ],
        []
    );

    // 顶部 KPI — 优先用筛选全集合计，避免只加当前页
    const kpiStats = useMemo(() => {
        const summary = data?.summary;
        if (summary && typeof summary.total_cost === "number") {
            return {
                totalCost: summary.total_cost,
                legCount: summary.leg_count,
                avgCost: summary.avg_cost,
                zeroLegs: summary.zero_legs,
            };
        }
        const items = data?.items || [];
        const costKeys = [
            "fuel", "navigation", "ground_handling", "landing_parking",
            "catering", "transportation", "traffic_rights_permit", "traffic_rights",
            "positioning", "uld", "sita", "deicing", "accommodation", "laundry",
            "disinfection", "flight_service_charge", "trucking_fee", "cargo_handling",
            "acmi", "commission", "others_fee",
        ];
        let totalCost = 0;
        let zeroLegs = 0;
        for (const it of items) {
            let legSum = 0;
            for (const k of costKeys) {
                const v = parseFloat(it[k]);
                if (Number.isFinite(v) && v !== 0) legSum += v;
            }
            totalCost += legSum;
            if (legSum === 0) zeroLegs += 1;
        }
        const legCount = items.length;
        const avgCost = legCount > 0 ? totalCost / legCount : 0;
        return { totalCost, legCount, avgCost, zeroLegs };
    }, [data]);

    // 录入费用:带出费用类型 + 航段信息(供"点单元格直接录入"和"行操作按钮"共用)
    // costTypeKey 为前端列 key(小写,如 "fuel");传 undefined 时为不预填(行级通用录入)
    const openCostEntry = useCallback((record, costTypeKey) => {
        setSelectedLeg(record);
        setPrefilledCostType(costTypeKey || undefined);
        setDrawerVisible(true);
    }, []);

    // S4 — 打开单元格下钻抽屉
    const openDrillDown = useCallback((record, costTypeKey, batchId = null) => {
        const backendCostType = COL_KEY_TO_COST_TYPE[costTypeKey];
        if (!backendCostType) {
            console.warn("未知费用列 key,无法打开下钻:", costTypeKey);
            return;
        }
        setDrillDownState({
            visible: true,
            flightOperationId: record?.flight_operation_id,
            costType: backendCostType,
            batchId,
            record,
        });
    }, []);

    // 渲染费用金额的通用方法 — 方案 A:去链接化 + 千分位 + tabular-nums
    // spec S2 (2026-05-27) — BH/FH 单元格点击改弹"数据来源 Modal",其它费用类型仍走原账单抽屉
    const renderCostAmount = (value, record, costType) => {
        const amount = parseFloat(value);
        const hasValue = Number.isFinite(amount) && amount !== 0;
        const isHours = costType?.isHours === true;

        const handleClick = (e) => {
            e.stopPropagation();
            if (!record?.flight_operation_id) return;
            if (isHours) {
                setBhFhDetailModal({
                    visible: true,
                    record,
                    costType: costType.key,
                });
                return;
            }
            // 空单元格(尚无账单)— 直接打开录入弹窗,并把该列费用类型 + 航段带进去,
            // 省去"再选一次费用类型"。有金额的单元格仍走下钻抽屉(看明细/管理已有账单)。
            if (!hasValue) {
                openCostEntry(record, costType.key);
                return;
            }
            openDrillDown(record, costType.key, locatedAnchor?.batchId ?? null);
        };

        if (!hasValue) {
            return (
                <span
                    onClick={handleClick}
                    className={styles.amountEmpty}
                    title={isHours
                        ? "无飞行记录、无 OOOI 数据 — 点击查看详情"
                        : `点击录入 ${costType.title} 费用`}
                >
                    —
                </span>
            );
        }

        return (
            <span
                onClick={handleClick}
                className={`${styles.amount}${isHours ? " " + styles.amountBhFh : ""}`}
                title={isHours ? "点击查看 BH/FH 数据来源" : "点击查看明细下钻"}
            >
                {!isHours && <span className={styles.amountCurrency}>$</span>}
                {formatMoney(amount)}
                {/* BH/FH 后缀 "H" 由 .amountBhFh::after 自动追加 — 不要在 JSX 里再加,会双倍 */}
            </span>
        );
    };

    // 表格列定义 - 航段聚合模式(扁平顺序,与旧版一致)
    // 视觉美化保留(千分位、tabular-nums、悬停下钻、表头粘性、zebra),业务族分组已移除
    const columns = [
        {
            title: "日期",
            dataIndex: "operation_date",
            key: "operation_date",
            width: 100,
            align: "center",
            fixed: "left",
            sorter: (a, b) => {
                if (a.operation_date === "未知" && b.operation_date === "未知") return 0;
                if (a.operation_date === "未知") return 1;
                if (b.operation_date === "未知") return -1;
                return new Date(a.operation_date) - new Date(b.operation_date);
            },
            defaultSortOrder: "ascend",
        },
        {
            title: "航班号",
            dataIndex: "flight_number",
            key: "flight_number",
            width: 80,
            align: "center",
            fixed: "left",
        },
        {
            title: "航段",
            dataIndex: "leg",
            key: "leg",
            width: 100,
            align: "center",
            fixed: "left",
        },
        {
            title: "飞机",
            dataIndex: "aircraft_registration",
            key: "aircraft_registration",
            width: 100,
            align: "center",
            fixed: "left",
            render: (text) => <AircraftTag registration={text} />,
        },
        // 18 个 cost_type 列(扁平,顺序严格按 COST_TYPES — 不要重排)
        ...COST_TYPES.map((costType) => {
            let columnWidth = 100;
            if (costType.title.length > 13) columnWidth = 145;
            else if (costType.title.length > 8) columnWidth = 120;

            return {
                title: costType.title,
                dataIndex: costType.key,
                key: costType.key,
                width: columnWidth,
                align: "right",
                render: (value, record) => renderCostAmount(value, record, costType),
            };
        }),
        {
            title: "操作",
            key: "action",
            fixed: "right",
            width: 100,
            align: "center",
            render: (_, record) => (
                <Space size="small">
                    <PermissionGuard permission="settlement:update">
                        <Button
                            type="primary"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEditCost(record);
                            }}
                        >
                            录入费用
                        </Button>
                    </PermissionGuard>
                </Space>
            ),
        },
    ];

    // 原来的列定义(暂时保留作为备份,后续 PR 删除)
    // eslint-disable-next-line no-unused-vars
    const oldColumns = [
        {
            title: "执行日期",
            dataIndex: "operation_date",
            key: "operation_date",
            width: 120,
            fixed: "left",
            sorter: (a, b) => {
                // 处理"未知"日期，将其排到最后
                if (a.operation_date === "未知" && b.operation_date === "未知") return 0;
                if (a.operation_date === "未知") return 1;
                if (b.operation_date === "未知") return -1;
                
                // 正常日期比较
                return new Date(a.operation_date) - new Date(b.operation_date);
            },
            defaultSortOrder: 'ascend',
        },
        {
            title: "航班号",
            dataIndex: "flight_number",
            key: "flight_number",
            width: 100,
            fixed: "left",
        },
        {
            title: "航段",
            dataIndex: "leg",
            key: "leg",
            width: 120,
            fixed: "left",
        },
        {
            title: "飞机注册号",
            dataIndex: "aircraft_registration",
            key: "aircraft_registration",
            width: 120,
        },
        {
            title: "费用类型",
            dataIndex: "cost_type",
            key: "cost_type",
            width: 140,
            render: (value) => {
                // 处理数据库中的枚举格式，显示友好的英文名称
                const typeMap = {
                    // 数据库枚举格式（全大写）转换为友好英文名称
                    'GROUND_HANDLING': 'Ground Handling',
                    'FUEL': 'Fuel',
                    'LANDING_PARKING': 'Landing/Parking',
                    'CATERING': 'Catering',
                    'TRANSPORTATION': 'Transportation',
                    'DEICING': 'Deicing',
                    'NAVIGATION': 'Navigation',
                    'POSITIONING': 'Positioning',
                    'ACCOMMODATION': 'Accommodation',
                    'ULD': 'ULD',
                    'TRAFFIC_RIGHTS_PERMIT': 'Traffic Rights Permit',
                    'SITA': 'SITA',
                    'OTHERS_FEE': 'Others Fee',
                    'CARGO_HANDLING': 'Cargo Handling',
                    'LAUNDRY': 'Laundry',
                    'DISINFECTION': 'Disinfection',
                    'FLIGHT_SERVICE_CHARGE': 'Flight Service Charge',
                    'COMMISSION': 'Commission',
                    'ACMI': 'ACMI',
                    'TRAFFIC_RIGHTS': 'Traffic Rights',
                    'TRUCKING_FEE': 'Trucking Fee',
                    // 兼容小写格式
                    'ground_handling': 'Ground Handling',
                    'fuel': 'Fuel',
                    'landing_parking': 'Landing/Parking',
                    'catering': 'Catering',
                    'transportation': 'Transportation',
                    'deicing': 'Deicing',
                    'navigation': 'Navigation',
                    'positioning': 'Positioning',
                    'accommodation': 'Accommodation',
                    'uld': 'ULD',
                    'traffic_rights_permit': 'Traffic Rights Permit',
                    'sita': 'SITA',
                    'others_fee': 'Others Fee',
                    'cargo_handling': 'Cargo Handling',
                    'laundry': 'Laundry',
                    'disinfection': 'Disinfection',
                    'flight_service_charge': 'Flight Service Charge',
                    'commission': 'Commission',
                    'acmi': 'ACMI',
                    'traffic_rights': 'Traffic Rights',
                    'trucking_fee': 'Trucking Fee'
                };
                return typeMap[value] || value;
            }
        },
        {
            title: "货币",
            dataIndex: "currency",
            key: "currency",
            width: 80,
        },
        {
            title: "金额(USD)",
            dataIndex: "amount",
            key: "amount",
            width: 120,
            align: "right",
            render: (value) => {
                const amount = parseFloat(value) || 0;
                return (
                    <span style={{ 
                        color: amount === 0 ? "#ff4d4f" : "#1890ff",
                        fontWeight: amount === 0 ? "normal" : "500",
                    }}>
                        {amount.toFixed(2)}
                    </span>
                );
            },
        },
        {
            title: "操作",
            key: "action",
            fixed: "right",
            width: 120,
            render: (_, record) => (
                <PermissionGuard permission="settlement:update">
                    <Button
                        type="primary"
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEditCost(record);
                        }}
                    >
                        录入费用
                    </Button>
                </PermissionGuard>
            ),
        },
    ];

    // 处理搜索
    const handleSearch = useCallback((values) => {
        const params = {};

        if (values.aircraft_registration) {
            params.aircraft_registration = values.aircraft_registration;
        }

        if (values.flight_number) {
            params.flight_number = values.flight_number;
        }

        if (values.cost_type) {
            params.cost_type = values.cost_type;
        }

        if (values.operation_date) {
            params.operation_date = values.operation_date.format("YYYY-MM-DD");
        }

        if (values.date_range && values.date_range.length === 2) {
            params.start_date = values.date_range[0].format("YYYY-MM-DD");
            params.end_date = values.date_range[1].format("YYYY-MM-DD");
        }

        if (values.departure_airport) {
            params.departure_airport = values.departure_airport;
        }

        if (values.arrival_airport) {
            params.arrival_airport = values.arrival_airport;
        }

        setFilterParams(params);
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    // 处理重置 - 重置到默认日期范围
    const handleReset = useCallback(() => {
        setFilterParams({
            start_date: defaultDateConfig.start_date,
            end_date: defaultDateConfig.end_date
        });
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, [defaultDateConfig]);

    const handleRefresh = useCallback(() => {
        message.info("正在刷新数据...");
        loadLegCosts({
            page: pagination.current,
            pageSize: pagination.pageSize,
            ...filterParams,
        });
    }, [loadLegCosts, pagination.current, pagination.pageSize, filterParams]);

    // 处理录入费用(行级操作按钮)— 不绑定具体费用列,清空预填让用户自选费用类型
    const handleEditCost = useCallback((record) => {
        setSelectedLeg(record);
        setPrefilledCostType(undefined);
        setDrawerVisible(true);
    }, []);

    // 处理抽屉成功 — 关闭弹窗并刷新当前列表(让刚录入的费用立刻显示在对应单元格)
    // 注:成功提示由 LegCostModal 内部按"新增/更新"分别弹出,这里不再重复 toast
    const handleDrawerSuccess = () => {
        setDrawerVisible(false);
        setSelectedLeg(null);
        loadLegCosts({
            page: pagination.current,
            pageSize: pagination.pageSize,
            ...filterParams,
        });
    };

    const handleTableChange = useCallback((newPagination) => {
        setPagination((prev) => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        }));
        const params = {
            page: newPagination.current,
            pageSize: newPagination.pageSize,
            ...filterParams
        };
        loadLegCosts(params);
    }, [loadLegCosts, filterParams]);

    // 导出 Excel — 按当前筛选条件拉取全部航段(后端 page_size 上限 100,循环翻页),
    // 再用客户端样式化导出(与列表页列、顺序、表头一致)。导出全量而非当前页。
    const handleExport = useCallback(async () => {
        if (exporting) return;
        setExporting(true);
        const hide = message.loading("正在准备导出数据...", 0);
        try {
            const EXPORT_PAGE_SIZE = 100;
            const MAX_PAGES = 200; // 安全阀:最多 2 万条,防止异常分页导致死循环
            let current = 1;
            let total = Infinity;
            const allItems = [];

            while (allItems.length < total && current <= MAX_PAGES) {
                const response = await settlementService.legCost.getLegCostList({
                    page: current,
                    page_size: EXPORT_PAGE_SIZE,
                    ...filterParams,
                });
                const responseData = response.data;
                const pageItems = Array.isArray(responseData?.items) ? responseData.items : [];
                allItems.push(...pageItems);
                total = responseData?.pagination?.total ?? allItems.length;
                if (pageItems.length === 0) break;
                current += 1;
            }

            if (allItems.length === 0) {
                message.warning("当前筛选条件下没有可导出的数据");
                return;
            }

            exportLegCostListToExcel(allItems, COST_TYPES, {
                filename: generateExportFilename("航段费用"),
            });
            message.success(`已导出 ${allItems.length} 条航段`);
        } catch (error) {
            console.error("导出航段费用失败:", error);
            message.error("导出失败: " + (error.message || "未知错误"));
        } finally {
            hide();
            setExporting(false);
        }
    }, [exporting, filterParams, COST_TYPES]);

    // 打开导入模态框
    const handleOpenImportModal = () => {
        setImportModalVisible(true);
    };

    const handleImportSuccess = () => {
        loadLegCosts({
            page: pagination.current,
            pageSize: pagination.pageSize,
            ...filterParams,
        });
    };

    // 处理行选择变化
    const handleRowSelectionChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    // 批量删除处理 — 打开弹窗二选一(全部删除 / 选费项删除),两个分支都走软删除
    const handleBatchDelete = () => {
        if (selectedRowKeys.length === 0) {
            message.warning("请先选择要删除的记录");
            return;
        }
        setBatchDeleteModalVisible(true);
    };

    // 批量删除成功:用后端真实 deleted_count 提示 + 受影响批次「前往刷新」通知 + 清空选中 + 刷新列表
    const handleBatchDeleteSuccess = (result) => {
        setBatchDeleteModalVisible(false);
        const deletedCount = result?.deleted_count ?? 0;
        const keptAdjustments = result?.skipped_adjustment_count ?? 0;
        if (keptAdjustments > 0) {
            message.warning(result?.message || "ACMI 调整没有删除。要去掉请到 ACMI 调整里撤回那张单据。");
        } else {
            message.success(result?.message || `成功删除 ${deletedCount} 条费项`);
        }

        // 删除同样改变了批次成本 —— 后端已把受影响的已完成批次标记为"需重新计算",
        // 这里提示用户前往批次核算页刷新(与录入/编辑费用的体验一致)。
        const affectedBatchIds = result?.affected_batch_ids || [];
        if (affectedBatchIds.length > 0) {
            notifyApi.info({
                message: "批次需要重新计算",
                description: `删除费用已影响 ${affectedBatchIds.length} 个批次,需要重新计算`,
                duration: 8,
                btn: (
                    <Space>
                        <Button size="small" onClick={() => notifyApi.destroy()}>
                            稍后处理
                        </Button>
                        <Button
                            type="primary"
                            size="small"
                            icon={<ArrowRightOutlined />}
                            onClick={() => {
                                notifyApi.destroy();
                                navigate("/settlement/batch-calculations");
                            }}
                        >
                            前往刷新
                        </Button>
                    </Space>
                ),
            });
        }

        setSelectedRowKeys([]);
        loadLegCosts({
            page: pagination.current,
            pageSize: pagination.pageSize,
            ...filterParams,
        });
    };

    // 如果认证信息还在加载中，显示加载状态
    if (authLoading) {
        return (
            <div className="page-container">
                <Card className="data-card">
                    <div style={{ textAlign: "center", padding: "50px 0" }}>
                        <Spin size="large" />
                        <p style={{ marginTop: 16 }}>正在加载用户信息...</p>
                    </div>
                </Card>
            </div>
        );
    }

    // 权限检查
    if (!hasPermission("settlement:read")) {
        return (
            <div className="page-container">
                <Card className="data-card">
                    <div style={{ textAlign: "center", padding: "50px 0" }}>
                        <h3>无权限访问</h3>
                        <p>您没有查看航段费用的权限，请联系管理员。</p>
                        <p style={{ color: "#666", fontSize: "12px", marginTop: 16 }}>
                            当前用户: {user?.username || "未知"}
                        </p>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="page-container">
            {contextHolder}
            {/* 页面标题区域 */}
            <div className="page-header-compact">
                <div className="page-title-compact">航段费用管理</div>
                <div className="page-action-compact">
                    <Space>
                        {selectedRowKeys.length > 0 && (
                            <PermissionGuard permission="settlement:delete">
                                <Button
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={handleBatchDelete}
                                >
                                    批量删除 ({selectedRowKeys.length})
                                </Button>
                            </PermissionGuard>
                        )}
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={handleExport}
                            loading={exporting}
                            disabled={loading || (data?.items?.length || 0) === 0}
                        >
                            导出Excel
                        </Button>
                        <PermissionGuard permission="settlement:import">
                            <Button type="primary" icon={<UploadOutlined />} onClick={handleOpenImportModal}>
                                导入Excel
                            </Button>
                        </PermissionGuard>
                    </Space>
                </div>
            </div>

            {/* 顶部 KPI 卡片(方案 A — 当前页快速统计) */}
            <div className={styles.kpiRow}>
                <div className={`${styles.kpiCard} ${styles.kpiCardCost}`}>
                    <div className={styles.kpiLabel}>筛选总成本</div>
                    <div className={styles.kpiValue}>
                        <span className={styles.kpiUnit}>$</span>
                        {formatMoney(kpiStats.totalCost)}
                    </div>
                    <div className={styles.kpiHint}>基准币(USD)合计</div>
                </div>
                <div className={`${styles.kpiCard} ${styles.kpiCardCount}`}>
                    <div className={styles.kpiLabel}>航段数</div>
                    <div className={styles.kpiValue}>
                        {kpiStats.legCount}
                        <span className={styles.kpiUnit}>段</span>
                    </div>
                    <div className={styles.kpiHint}>当前筛选条件下</div>
                </div>
                <div className={`${styles.kpiCard} ${styles.kpiCardAvg}`}>
                    <div className={styles.kpiLabel}>平均每段</div>
                    <div className={styles.kpiValue}>
                        <span className={styles.kpiUnit}>$</span>
                        {formatMoney(kpiStats.avgCost)}
                    </div>
                    <div className={styles.kpiHint}>总成本 / 航段数</div>
                </div>
                <div className={`${styles.kpiCard} ${styles.kpiCardZero}`}>
                    <div className={styles.kpiLabel}>零成本航段</div>
                    <div className={styles.kpiValue}>
                        {kpiStats.zeroLegs}
                        <span className={styles.kpiUnit}>段</span>
                    </div>
                    <div className={styles.kpiHint}>可能存在漏录</div>
                </div>
            </div>

            {/* 搜索过滤 */}
            <Card className="filter-card">
                <LegCostFilter
                    onSearch={handleSearch}
                    onReset={handleReset}
                    onRefresh={handleRefresh}
                    defaultDateRange={defaultDateConfig.defaultDateRange}
                />
            </Card>

            {/* 2026-05-13 决策 14:从异常工作台「定位」按钮跳过来时的提示条 */}
            {locatedAnchor && (
                <Alert
                    type="info"
                    showIcon
                    icon={<AimOutlined />}
                    style={{ marginBottom: 12 }}
                    message={
                        <Space size="small" wrap>
                            <span>已定位到航段 #{locatedAnchor.flightOperationId},请重点查看</span>
                            <Tag color="blue">{locatedAnchor.costType}</Tag>
                            <span>列(列表已过滤为单条;点&quot;显示所有&quot;恢复)</span>
                        </Space>
                    }
                    action={
                        <Button size="small" type="link" onClick={handleClearLocation}>
                            显示所有
                        </Button>
                    }
                    closable
                    onClose={handleClearLocation}
                />
            )}

            {/* 费用列表 */}
            <Card className="data-card" bodyStyle={{ padding: 0 }}>
                <div className={styles.tableWrapper}>
                    <Table
                        size="small"
                        bordered={false}
                        columns={columns}
                        dataSource={data?.items || []}
                        loading={loading}
                        rowKey="id"
                        sticky
                        rowClassName={(_, idx) => (idx % 2 === 0 ? styles.rowEven : styles.rowOdd)}
                        rowSelection={{
                            selectedRowKeys,
                            onChange: handleRowSelectionChange,
                            checkStrictly: false,
                        }}
                        pagination={{
                            current: data?.pagination?.current || 1,
                            pageSize: data?.pagination?.pageSize || 20,
                            total: data?.pagination?.total || 0,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total) => `共 ${total} 条`,
                            pageSizeOptions: ["10", "20", "50", "100", "300", "1000"],
                        }}
                        onChange={handleTableChange}
                        // 固定总宽 2700px:23 列 width 之和约 2550,留余量防止字段挤压
                        // 注意:fixed 列(日期/航班/航段/飞机 + 操作)必须配合具体数字才能正确粘左/粘右
                        scroll={{ x: 2700, y: undefined }}
                        tableLayout="fixed"
                    />
                </div>
            </Card>

            {/* 费用录入模态框 */}
            <LegCostModal
                visible={drawerVisible}
                legData={selectedLeg}
                prefilledCostType={prefilledCostType}
                onSuccess={() => {
                    handleDrawerSuccess();
                    setPrefilledCostType(undefined);
                }}
                onCancel={() => {
                    setDrawerVisible(false);
                    setSelectedLeg(null);
                    setPrefilledCostType(undefined);
                }}
            />

            {/* S4 下钻抽屉(T21-T26b) */}
            <LegCostDrillDownDrawer
                visible={drillDownState.visible}
                flightOperationId={drillDownState.flightOperationId}
                costType={drillDownState.costType}
                batchId={drillDownState.batchId}
                onClose={() =>
                    setDrillDownState((prev) => ({ ...prev, visible: false }))
                }
                onAddCost={(costTypeBackend) => {
                    // T26a — 抽屉里点"在此录入费用",切换到 LegCostModal,预填费用类型 + 航段
                    // 用抽屉打开时存下的整条记录,保证航段(航班号/航段/日期)能正常带出
                    const flightOpId = drillDownState.flightOperationId;
                    const rec = drillDownState.record || {
                        flight_operation_id: flightOpId,
                        id: flightOpId,
                    };
                    setDrillDownState((prev) => ({ ...prev, visible: false }));
                    openCostEntry(rec, String(costTypeBackend).toLowerCase());
                }}
                onAfterDelete={() => {
                    // 删除后刷新主表(实时合计变了)
                    loadLegCosts({
                        page: pagination.current,
                        pageSize: pagination.pageSize,
                        ...filterParams,
                    });
                }}
                // 注:重算入口已迁出本抽屉(2026-05 决策 17),不再需要 onAfterRecompute
            />

            {/* Excel导入模态框 */}
            <FrontendImportModal
                isOpen={importModalVisible}
                onClose={() => setImportModalVisible(false)}
                onSuccess={handleImportSuccess}
                importType="leg_cost_import"
            />

            {/* spec S2 — BH/FH 数据来源详情 Modal */}
            <BhFhDetailModal
                visible={bhFhDetailModal.visible}
                record={bhFhDetailModal.record}
                costType={bhFhDetailModal.costType}
                onClose={() => setBhFhDetailModal({ visible: false, record: null, costType: null })}
                onJumpToFlightRecord={() => {
                    setBhFhDetailModal({ visible: false, record: null, costType: null });
                    navigate("/settlement/flight-record-log");
                }}
            />

            {/* 批量删除弹窗:全部删除 / 选费项删除 双分支(均走软删除) */}
            <BatchDeleteModal
                visible={batchDeleteModalVisible}
                flightOperationIds={selectedRowKeys}
                onClose={() => setBatchDeleteModalVisible(false)}
                onSuccess={handleBatchDeleteSuccess}
            />

        </div>
    );
};

/**
 * spec S2 (2026-05-27) — BH/FH 数据来源详情 Modal
 *
 * 业务说明:
 * - BH/FH 不再从 invoice_line_item 取数 (spec D3),改读 settlement_flight_data + MVT 兜底
 * - 因此点击 BH/FH 单元格不应该打开"账单明细抽屉"(那个抽屉查的是 invoice 行)
 * - 改为弹窗显示数值 + 来源 + 跳转飞行记录管理入口
 *
 * 数据来源:全部来自主表 record,不需要额外 API
 */
const BhFhDetailModal = ({ visible, record, costType, onClose, onJumpToFlightRecord }) => {
    if (!record || !costType) return null;

    const isB = costType === "bh";
    const value = isB ? record.bh : record.fh;
    const source = isB ? record.bh_source : record.fh_source;
    const label = isB ? "BH (Block Hours, 含滑行)" : "FH (Flight Hours, 仅飞行)";

    const SOURCE_META = {
        FLIGHT_RECORD: {
            color: "green",
            text: "飞行记录",
            tip: "数值来自「飞行记录管理」录入的 SETTLEMENT_BH/FH 字段(优先级最高)",
        },
        MVT: {
            color: "blue",
            text: "OOOI 计算",
            tip: "无飞行记录数据,数值由 OOOI 时间 (settlement_atd/ata 或 flight_operation.atd/ata) 计算得出",
        },
        NONE: {
            color: "default",
            text: "暂无数据",
            tip: "既无飞行记录、也无 OOOI 时间。请在「飞行记录管理」录入或补齐飞机日志数据",
        },
    };
    const meta = SOURCE_META[source] || SOURCE_META.NONE;
    const hasValue = Number.isFinite(Number(value)) && Number(value) > 0;

    return (
        <Modal
            open={visible}
            title={
                <Space>
                    <FieldTimeOutlined />
                    {label} — 数据来源
                </Space>
            }
            onCancel={onClose}
            footer={[
                <Button key="close" onClick={onClose}>关闭</Button>,
                <Button
                    key="jump"
                    type="primary"
                    icon={<ArrowRightOutlined />}
                    onClick={onJumpToFlightRecord}
                >
                    前往飞行记录管理
                </Button>,
            ]}
            width={520}
        >
            <Descriptions
                bordered
                column={1}
                size="small"
                labelStyle={{ width: 110, background: "#fafafa" }}
            >
                <Descriptions.Item label="航段">
                    <Typography.Text strong>{record.flight_number}</Typography.Text>
                    {"  "}
                    <Typography.Text type="secondary">{record.leg}</Typography.Text>
                    {"  "}
                    <Typography.Text type="secondary">{record.operation_date}</Typography.Text>
                </Descriptions.Item>
                <Descriptions.Item label="飞机">
                    {record.aircraft_registration || "—"}
                </Descriptions.Item>
                <Descriptions.Item label={isB ? "BH" : "FH"}>
                    <Typography.Text
                        strong
                        style={{
                            fontSize: 20,
                            color: hasValue ? "#1677ff" : "#bfbfbf",
                            fontFamily: "tabular-nums",
                        }}
                    >
                        {hasValue ? `${Number(value).toFixed(2)} H` : "—"}
                    </Typography.Text>
                </Descriptions.Item>
                <Descriptions.Item label="数据来源">
                    <Tag color={meta.color}>{meta.text}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="说明">
                    <Typography.Text type="secondary">{meta.tip}</Typography.Text>
                </Descriptions.Item>
            </Descriptions>
            <Alert
                style={{ marginTop: 16 }}
                type="info"
                showIcon
                message="BH/FH 数据通路已迁移"
                description={
                    <span>
                        2026-05 后 BH/FH 不再通过 BH.xlsx / FH.xlsx 上传,
                        统一通过<Typography.Text strong>「飞行记录管理」</Typography.Text>录入飞机日志数据维护。
                        历史 invoice 中的 BH/FH 行已不再读取。
                    </span>
                }
            />
        </Modal>
    );
};

export default LegCostManagementPage;

